import React, { useState, useEffect } from "react";
import { useHistory } from "react-router";
import moment from "moment";
import { Card, Table, Tabs, Pagination, Popconfirm, message, Button, Input, Select, DatePicker } from "antd";
import { columns } from "./tableConfigs";
import Service from "../../services/users";

const Users = () => {
  
  const history = useHistory();

  const TabPane = Tabs.TabPane;
  const { Search } = Input;
  const Option = Select.Option;

  const [data, setData] = useState([]);
  const [isActive, setIsActive] = useState("all");
  const [totalCount, setTotalCount] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search_text, setSearch_text] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredInfo, setFilteredInfo] = useState(null);
  const [sortedInfo, setSortedInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [accountType, setAccountType] = useState("all");

  const handleStartDateChange = (date) => {
    let newStartDate = date ? date.startOf("day").toISOString() : null;
    let newEndDate = endDate;

    if (date && endDate && date.endOf("day").isAfter(moment(endDate))) {
      newEndDate = null;
      setEndDate(null);
    }

    setStartDate(newStartDate);
    onSearchPress({ fromDate: newStartDate, toDate: newEndDate });
  };

  const handleEndDateChange = (date) => {
    let newEndDate = date ? date.endOf("day").toISOString() : null;
    let newStartDate = startDate;

    if (date && startDate && moment(startDate).isAfter(date.endOf("day"))) {
      newStartDate = null;
      setStartDate(null);
    }

    setEndDate(newEndDate);
    onSearchPress({ fromDate: newStartDate, toDate: newEndDate });
  };

  useEffect(() => {
    getUsersData();
  }, []);

  const getUsersData = async () => {
    try {
      let response = await fetchData({
        searchTerm: search_text,
        userFilter: isActive,
        pageIndex: pageIndex,
        pageSize: pageSize,
        fromDate: startDate,
        toDate: endDate,
        accountType: accountType,
      });

      setData(response.data || []);
      setTotalCount(response?.total || 0);
    } catch (error) {
      console.error("Error in getUsersData:", error);
      message.error("Failed to load users");
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }

  const onEdit = (e, id) => {
    e.preventDefault();
    history.push(`/userEditForm/${id}`);
  }

  const renderActionButtons = (userId) => (
    <div className="action-btn">
      <a href="" title="Edit" onClick={(e) => onEdit(e, userId)}>
        <i className="fa fa-pencil"></i>
      </a>
      <a href="" title="Delete">
        <Popconfirm
          title="Are you sure delete this user?"
          onConfirm={async () => {
            try {
              let response = await Service.deleteUser(userId);

              if (response.message === "user.userDeleted") {
                message.success("User Deleted!");
                let refreshResponse = await fetchData({
                  searchTerm: search_text,
                  userFilter: isActive,
                  pageSize: pageSize,
                  pageIndex: pageIndex,
                  fromDate: startDate,
                  toDate: endDate,
                  accountType: accountType,
                });
                setData(refreshResponse.data || []);
                setTotalCount(refreshResponse?.total || 0);
                setLoading(false);
              } else {
                message.error("An unexpected error has occured while deleting user.");
              }
            } catch (error) {
              message.error("Failed to delete user.");
              setLoading(false);
            }
          }}
          onCancel={() => { }}
          okText="Yes"
          cancelText="No"
        >
          <span className="gx-link" title="Delete">
            <i className="icon icon-trash"></i>
          </span>
        </Popconfirm>
      </a>
    </div>
  );

  const fetchData = async (params) => {
    setLoading(true);
    
    try {
      const users = await Service.getActiveUsers(
        params?.searchTerm || "",
        null,
        params?.accountType || "all",
        null,
        null,
        params?.pageIndex || 0,
        params?.pageSize || 10,
        params?.userFilter || "all",
        params?.fromDate || null,
        params?.toDate || null,
        false
      );

      if (!users || !users.data) {
        return { data: [], total: 0 };
      }

      let data = await Promise.all(
        users.data.map(async (item, index) => {
          try {
            const userDetails = await Service.getUserDetails(item._id);
            
            return {
              key: item._id,
              id: index + 1 + (params?.pageIndex || 0) * (params?.pageSize || 10),
              name: userDetails?.name || item.name || "N/A",
              email: userDetails?.email || item.email || "N/A",
              createdAt: userDetails?.createdAt ? moment(userDetails.createdAt).format("YYYY-MM-DD") : (item.createdAt ? moment(item.createdAt).format("YYYY-MM-DD") : "N/A"),
              updatedAt: userDetails?.updatedAt ? moment(userDetails.updatedAt).format("YYYY-MM-DD") : (item.updatedAt ? moment(item.updatedAt).format("YYYY-MM-DD") : "N/A"),
              isVerified: userDetails?.isVerified !== undefined ? (userDetails.isVerified ? "Yes" : "No") : (item.isVerified ? "Yes" : "No"),
              isPremium: userDetails?.isPremium !== undefined ? (userDetails.isPremium ? "Yes" : "No") : (item.isPremium ? "Yes" : "No"),
              membershipExpiry: userDetails?.membershipExpiry
                ? moment(userDetails.membershipExpiry).format("YYYY-MM-DD")
                : item.membershipExpiry
                  ? moment(item.membershipExpiry).format("YYYY-MM-DD")
                  : "N/A",
              action: renderActionButtons(item._id),
            };
          } catch (error) {
            console.error(`Error fetching details for user ${item._id}:`, error);
            return {
              key: item._id,
              id: index + 1 + (params?.pageIndex || 0) * (params?.pageSize || 10),
              name: item.name || "N/A",
              email: item.email || "N/A",
              createdAt: item.createdAt ? moment(item.createdAt).format("YYYY-MM-DD") : "N/A",
              updatedAt: item.updatedAt ? moment(item.updatedAt).format("YYYY-MM-DD") : "N/A",
              isVerified: item.isVerified ? "Yes" : "No",
              isPremium: item.isPremium ? "Yes" : "No",
              membershipExpiry: item.membershipExpiry
                ? moment(item.membershipExpiry).format("YYYY-MM-DD")
                : "N/A",
              action: renderActionButtons(item._id),
            };
          }
        })
      );

      return { data: data || [], total: users?.total || 0 };
    } catch (error) {
      console.error("Error fetching users:", error);
      message.error("Failed to fetch users");
      return { data: [], total: 0 };
    }
  };

  const onPageChange = async (page) => {
    setPageIndex(page - 1);
    setCurrentPage(page);
    setLoading(true);

    let result = await fetchData({
      searchTerm: search_text,
      userFilter: isActive,
      pageIndex: page - 1,
      pageSize: pageSize,
      fromDate: startDate,
      toDate: endDate,
      accountType: accountType,
    });

    setData(result.data || []);
    setTotalCount(result?.total || 0);
    setLoading(false);
  }

  const onSearchChange = (e) => setSearch_text(e.target.value);

  const onClear = () => {
    setIsActive("all");
    setPageIndex(0);
    setSearch_text("");
    setCurrentPage(1);
    setFilteredInfo(null);
    setSortedInfo(null);
    setVisible(false);
    setStartDate(null);
    setEndDate(null);
    setAccountType("all");
    onSearchPress();
  }

  const onSearchPress = async (arg) => {
    let overrides = {};

    if (typeof arg === "string") {
      overrides.searchTerm = arg;
      setSearch_text(arg);
    } else if (typeof arg === "object" && arg !== null) {
      overrides = arg;
    }

    const nextSearchTerm = overrides.searchTerm ?? search_text;
    const nextUserFilter = overrides.userFilter ?? isActive;
    const nextFromDate = overrides.fromDate ?? startDate;
    const nextToDate = overrides.toDate ?? endDate;
    const nextAccountType = overrides.accountType ?? accountType;

    setPageIndex(0);
    setCurrentPage(1);
    setLoading(true);

    try {
      let data = await fetchData({
        searchTerm: nextSearchTerm,
        userFilter: nextUserFilter,
        pageIndex: 0,
        pageSize: pageSize,
        fromDate: nextFromDate,
        toDate: nextToDate,
        accountType: nextAccountType,
      });

      setData(data.data || []);
      setTotalCount(data?.total || 0);
    } catch (error) {
      console.error("Error in onSearchPress:", error);
      message.error("Failed to search users");
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }

  const handleSelectChange = async (value) => {
    const filterValue = value === "active" ? "active" : value === "inactive" ? "inactive" : "all";
    setIsActive(filterValue);
    setLoading(true);
    setPageIndex(0);
    setCurrentPage(1);
    
    try {
      let result = await fetchData({
        searchTerm: search_text,
        userFilter: filterValue,
        pageIndex: 0,
        pageSize: pageSize,
        fromDate: startDate,
        toDate: endDate,
        accountType: accountType,
      });
      
      setData(result.data || []);
      setTotalCount(result?.total || 0);
    } catch (error) {
      console.error("Error fetching users after filter change:", error);
      message.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }

  const handleAccountTypeChange = async (value) => {
    setAccountType(value);
    setLoading(true);
    setPageIndex(0);
    setCurrentPage(1);

    try {
      const result = await fetchData({
        searchTerm: search_text,
        userFilter: isActive,
        pageIndex: 0,
        pageSize: pageSize,
        fromDate: startDate,
        toDate: endDate,
        accountType: value,
      });

      setData(result.data || []);
      setTotalCount(result?.total || 0);
    } catch (error) {
      console.error("Error fetching users after account type change:", error);
      message.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  let sorted = sortedInfo || {};

  let totalCounts = totalCount
    ? Math.ceil(totalCount / 10) * 10
    : 10;

  return (
    <div className="menu-editor-box card-pad-new menu-cate-list users-main-page">
      <div className="payout-report-box">
        <Card className="gx-card breadcrumb-box" title="">
          <div className="ant-row hd-title">
            <div className="ant-col-md-12">
              <h1>Users</h1>
            </div>
          </div>

          <Tabs
            type="card"
            className="tabs-main-box gx-mt-4"
          >
            <TabPane tab="Default" key="1">
              <div
                className="ant-col-md-24 rite-btn"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 12,
                  justifyContent: "end",
                }}
              >
                <div>
                  <DatePicker
                    allowClear
                    placeholder="Start date"
                    style={{ width: "100%" }}
                    value={startDate ? moment(startDate) : null}
                    onChange={handleStartDateChange}
                    format="YYYY-MM-DD"
                  />
                </div>
                <div>
                  <DatePicker
                    allowClear
                    placeholder="End date"
                    style={{ width: "100%" }}
                    value={endDate ? moment(endDate) : null}
                    onChange={handleEndDateChange}
                    format="YYYY-MM-DD"
                  />
                </div>
                <div>
                  <Select
                    value={accountType}
                    style={{ width: "150px" }}
                    onChange={handleAccountTypeChange}
                  >
                    <Option value="all">All Accounts</Option>
                    <Option value="free">Free</Option>
                    <Option value="paid">Paid</Option>
                  </Select>
                </div>
                <div>
                  <Search
                    placeholder="search"
                    style={{ width: "100%" }}
                    onSearch={onSearchPress}
                    onChange={onSearchChange}
                    value={search_text}
                  />
                </div>
                
                <Button className="my-btn-cust" onClick={onClear}>
                  Clear
                </Button>
                <a
                  className="my-btn-link more-link d-none"
                  onClick={() => setVisible(!visible)}
                >
                  More <i className="icon icon-chevron-down"></i>
                </a>
              </div>

              {visible && (
                <div className="ant-row gx-m-0 gx-mt-3 additional-filter">
                  <div className="ant-col-md-7 gx-mb-3 gx-pl-2 gx-pr-2">
                    <Select
                      showSearch
                      defaultValue="all"
                      placeholder="Status"
                      optionFilterProp="children"
                      onChange={handleSelectChange}
                      filterOption={(input, option) =>
                        option.props.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      <Option value="all">All</Option>
                      <Option value="active">Active</Option>
                      <Option value="inactive">Inactive</Option>
                    </Select>
                  </div>
                  <div className="ant-col-md-2 gx-mb-3 gx-pl-2 gx-pr-2">
                    <Button
                      onClick={onSearchPress}
                      className="my-btn-cust"
                    >
                      Search
                      </Button>
                  </div>
                </div>
              )}
              <div className="ant-row gx-mt-3 bulk-btn-option">
                <div className="ant-col-md-24 pageing-box">
                  <Pagination
                    simple
                    pageSize={10}
                    defaultCurrent={1}
                    total={totalCounts}
                    onChange={onPageChange}
                    current={currentPage}
                  />
                </div>
              </div>

              <div className="collaps-main">
                <div className="ant-row">
                  <div className="ant-col gx-order-sm-1 ant-col-xs-24 ant-col-sm-24 ant-col-md-24 ant-col-lg-24 ant-col-xl-24">
                    <Table
                      rowKey={(record) => {
                        return record?.key;
                      }}
                      pagination={{ pageSize: 10 }}
                      className="gx-table-responsive menu-cate-lisi-table sellads-table"
                      columns={columns({ sorted })}
                      loading={loading}
                      dataSource={data}
                      onChange={handleChange}
                    />
                    <div className="pageing-box gx-mt-4">
                      <Pagination
                        simple
                        pageSize={10}
                        defaultCurrent={1}
                        total={totalCounts}
                        onChange={onPageChange}
                        current={currentPage}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </TabPane>

          </Tabs>
        </Card>
      </div>
    </div>
  );
}

export default Users;

